// Turns a completed Applied Practice / "Practice Sheets" attempt set into a
// qualitative summary instead of a raw score fraction — no numbers, no
// percentages, matching the same "describe what's solid / what needs another
// look" rule used everywhere else in the guided-session redesign.
//
// skill_id is free-text the model invents per generation (no curated
// skills-lookup table exists), so grouping by it is a best-effort signal —
// falls back to a plain count-tier message when it isn't informative enough
// (e.g. everything tagged "GENERAL", or too few distinct tags among the
// wrong answers to say anything specific).

import type { AppliedQuestion, AppliedAttempt } from "../services/conceptPlus1Service";

export interface AppliedPracticeSummary {
  headline: string;
  detail?: string;
}

function isInformativeSkillId(skillId: string | undefined): boolean {
  if (!skillId) return false;
  const normalized = skillId.trim().toUpperCase();
  return normalized.length > 0 && normalized !== "GENERAL";
}

export function summarizeAppliedPractice(
  questions: AppliedQuestion[],
  attempts: Record<number, AppliedAttempt>
): AppliedPracticeSummary {
  const total = questions.length;
  const wrongSkillIds: string[] = [];
  let correctCount = 0;

  questions.forEach((q, idx) => {
    const attempt = attempts[idx];
    if (!attempt) return;
    if (attempt.correct) {
      correctCount += 1;
    } else if (isInformativeSkillId(q.skill_id)) {
      wrongSkillIds.push(q.skill_id!.trim());
    }
  });

  if (total === 0) {
    return { headline: "Practice complete" };
  }

  if (correctCount === total) {
    return { headline: "You've got this one down solidly." };
  }

  // Lead with the most frequent shaky skill_id, if grouping is informative.
  if (wrongSkillIds.length > 0) {
    const counts = new Map<string, number>();
    wrongSkillIds.forEach((id) => counts.set(id, (counts.get(id) ?? 0) + 1));
    const [topSkill] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    const readableSkill = topSkill.replace(/_/g, " ").toLowerCase();
    return {
      headline: "Worth another look:",
      detail: readableSkill,
    };
  }

  // Fallback: plain count-tier message, no numbers.
  const ratio = correctCount / total;
  if (ratio >= 0.7) {
    return { headline: "You handled most of these confidently." };
  }
  return { headline: "A few of these are worth revisiting." };
}
